import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { DocumentResult } from 'expo-document-picker';
import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import { ScheduleBillPayment } from '../enum';
import { displayAmountWithCurrency, isAmountPositive, toNumber } from '../utils/utils';
import { useBillPaymentCalcFee } from './useBillPaymentCalcFee.hook';
import { useBillPaymentCurrencies } from './useBillPaymentCurrencies.hook';
import { QuoteDataRequest, QuoteResponse, VERIFICATION_CODE } from './useCreateBillPayment.hook';

export interface UseBillPaymentFormProp {
  detailForm: {
    bank: string | undefined;
    bankAccountNumber: string | undefined;
    debitAccountWallerId: string | undefined;
    debitAccountWalletName: string | undefined;
    additionalDetail: string | undefined;
    assetName: string | undefined;
    amount: string | undefined;
    assetList: {
      id: number;
      label: string;
      value: string;
    }[];
    walletId: string;
    setBankName: (input: string | undefined) => void;
    setTransferPeerId: (input: string | undefined) => void;
    setBankAccountNumber: (input: string | undefined) => void;
    setDebitAccountWallerId: (input: string | undefined) => void;
    setDebitAccountWalletName: (input: string | undefined) => void;
    setAdditionalDetail: (input: string | undefined) => void;
    setAssetName: (input: string) => void;
    setAmount: (input: string | undefined) => void;
    setWalletId: (input: string) => void;
    onValidateVerification: (errorCode?: string) => boolean;
    setInstructionId: (input: string) => void;
  };
  scheduleForm: {
    scheduleTab: string;
    schedule: Moment | null;
    dateOfFirstPayment: Moment | null;
    interval: string | undefined;
    paymentEndDate: Moment | null;
    remarks: string | undefined;
    setScheduleTab: (input: string) => void;
    setSchedule: (input: Moment | null) => void;
    setDateOfFirstPayment: (input: Moment | null) => void;
    setInterval: (input: string) => void;
    setPaymentEndDate: (input: Moment | null) => void;
    onValidateScheduleForm: () => void;
    setRemarks: (input: string | undefined) => void;
  };
  general: {
    billPaymentState: BillPaymentState;
    validated: boolean;
    quoteDataRequest: QuoteDataRequest;
    debitAmount: string;
    billPaymentFee: string;
    userFee: string;
    isLoadingFee: boolean;
    quoteDataRequestProps: QuoteDataRequest | undefined;
    quoteResponseProps: QuoteResponse | undefined;
    documentSelected: DocumentResult | undefined;
    setDocumentSelected: (input: DocumentResult | undefined) => void;
    setQuoteDataRequestProps: (input: QuoteDataRequest) => void;
    setQuoteResponseProps: (input: QuoteResponse) => void;
    setValidated: (input: boolean) => void;
    setFlagSubmitted: (input: boolean) => void;
  };
}

export interface ErrorMessage {
  error: boolean;
  message: string | undefined;
}

export interface BillPaymentState {
  bank?: ErrorMessage;
  bankAccountNumber?: ErrorMessage;
  additionalDetail?: ErrorMessage;
  amount?: ErrorMessage;
  scheduleDate?: ErrorMessage;
  dateOfFirstPayment?: ErrorMessage;
  interval?: ErrorMessage;
  paymentEndDate?: ErrorMessage;
  wallet?: ErrorMessage;
}

export interface ScheduleInstruction {
  type: string;
  interval?: string;
  startDate?: Moment | null;
  endDate?: Moment | null;
}

export const useBillPaymentForm = (): UseBillPaymentFormProp => {
  const store = useStore();
  const { t } = useTranslation('earn');
  const [scheduleTab, setScheduleTab] = useState<string>(ScheduleBillPayment.NOW);
  const [instructionId, setInstructionId] = useState<string>('');
  const [schedule, setSchedule] = useState<Moment | null>(null);
  const [dateOfFirstPayment, setDateOfFirstPayment] = useState<Moment | null>(null);
  const [interval, setInterval] = useState<string>();
  const [paymentEndDate, setPaymentEndDate] = useState<Moment | null>(null);
  const [billPaymentState, setBillPaymentState] = useState<BillPaymentState>({});
  const [validated, setValidated] = useState<boolean>(false);
  const [bank, setBankName] = useState<string>();
  const [bankAccountNumber, setBankAccountNumber] = useState<string>();
  const [debitAccountWallerId, setDebitAccountWallerId] = useState<string>();
  const [debitAccountWalletName, setDebitAccountWalletName] = useState<string>();
  const [additionalDetail, setAdditionalDetail] = useState<string>();
  const [assetName, setAssetName] = useState<string>('HKD');
  const [amount, setAmount] = useState<string>();
  const [remarks, setRemarks] = useState<string>();
  const [transferPeerId, setTransferPeerId] = useState<string>();
  const [walletId, setWalletId] = useState<string>('');
  const [documentSelected, setDocumentSelected] = useState<DocumentResult>();
  const [flagSubmitted, setFlagSubmitted] = useState<boolean>(false);

  const wallets = store.wallets;

  const {
    billPaymentData: { fee, refetch, isLoading: isLoadingFee }
  } = useBillPaymentCalcFee({ amount, assetSymbol: assetName, enabledFetchBillPaymentFee: false });
  const { assetList } = useBillPaymentCurrencies();
  const { userFee: bpUserFee, feeCurrency: bpFeeCurrency } = fee || {};
  const [quoteDataRequestProps, setQuoteDataRequestProps] = useState<QuoteDataRequest>();
  const [quoteResponseProps, setQuoteResponseProps] = useState<QuoteResponse>();
  const walletLabel = useMemo(() => {
    if (walletId && wallets) {
      const walletsWithAsset = wallets.filter((wallet) => wallet.asset === assetName)?.[0];
      if (!walletsWithAsset) return '-';
      let index = walletsWithAsset.wallets.findIndex((item) => item.id === walletId);
      if (index != -1) {
        const label = walletsWithAsset.wallets[index].accountLabel
          ? t(`earn-invest::glossary.${walletsWithAsset.wallets[index].accountLabel}`)
          : walletsWithAsset.wallets[index].accountName;
        return `${label} (${walletsWithAsset.wallets[index].accountNumber})`;
      }
    }
    return '0';
  }, [walletId]);

  const debitAmount = useMemo(() => {
    if (!isAmountPositive(amount)) {
      return `${displayAmountWithCurrency('0', assetName)}`;
    }
    const newAmount = toNumber(amount);
    const newFee = parseFloat(bpUserFee ? bpUserFee : '0');
    const finalAmount = newAmount + newFee;
    return `${displayAmountWithCurrency(`${Math.abs(finalAmount)}`, assetName)}`;
  }, [amount, assetName, bpUserFee]);

  const billAmount = useMemo(() => {
    return `${displayAmountWithCurrency(_.isEmpty(amount) || !amount ? '0' : amount, assetName)}`;
  }, [amount, assetName]);

  const billPaymentFee = useMemo(() => {
    return `${displayAmountWithCurrency(bpUserFee ?? '0', bpFeeCurrency ?? assetName)}`;
  }, [amount, bpUserFee, bpFeeCurrency]);

  const quoteDataRequest: QuoteDataRequest = useMemo(() => {
    if (quoteDataRequestProps == undefined) {
      return {
        intent: 'PAYMENT',
        idempotencyKey: '',
        note: '',
        purpose: '',
        attributes: {
          paymentType: 'GLOBAL',
          attachments: [{ url: '', isDeleted: false, fileName: '', clientDocumentId: '' }]
        }
      };
    }

    const currentSchedule =
      scheduleTab !== ScheduleBillPayment.NOW
        ? {
            type: scheduleTab.toUpperCase(),
            startDate: formatDate(dateOfFirstPayment?.toISOString() ?? '', { format: DATE_FORMAT.NO_TIME }),
            endDate:
              scheduleTab === ScheduleBillPayment.FUTURE
                ? formatDate(schedule?.toISOString() ?? '', { format: DATE_FORMAT.NO_TIME })
                : scheduleTab === ScheduleBillPayment.RECURRING
                ? formatDate(paymentEndDate?.toISOString() ?? '', { format: DATE_FORMAT.NO_TIME })
                : undefined,
            interval: _.isEmpty(interval) ? undefined : interval!.toUpperCase()
          }
        : undefined;

    const rq: QuoteDataRequest = {
      id: instructionId,
      intent: quoteDataRequestProps.intent,
      idempotencyKey: quoteDataRequestProps.idempotencyKey,
      purpose: quoteDataRequestProps.purpose,
      remarks: remarks ?? quoteDataRequestProps.remarks,
      attributes: {
        amount: toNumber(amount).toString(),
        asset: assetName,
        attachments: quoteDataRequestProps.attributes.attachments,
        source: {
          type: quoteDataRequestProps.attributes.source?.type || 'ACCOUNT',
          attributes: {
            walletId: walletId
          }
        },
        destination: {
          type: quoteDataRequestProps.attributes.destination?.type || 'PAYEE',
          attributes: {
            recipient: bank,
            paymentInfo: additionalDetail,
            accountNumber: bankAccountNumber,
            paymentType: quoteDataRequestProps.attributes.destination?.attributes?.paymentType,
            transferPeerId: transferPeerId
          }
        },
        schedule: currentSchedule
      }
    };
    return rq;
  }, [
    instructionId,
    schedule,
    dateOfFirstPayment,
    interval,
    paymentEndDate,
    bank,
    bankAccountNumber,
    debitAccountWallerId,
    assetName,
    amount,
    additionalDetail,
    remarks,
    debitAmount,
    billAmount,
    billPaymentFee,
    walletLabel,
    walletId,
    scheduleTab,
    quoteDataRequestProps,
    transferPeerId
  ]);

  const refetchFee = useCallback(_.debounce(refetch, 1000), []);

  useEffect(() => {
    refetchFee();
  }, [amount, assetName]);

  useEffect(() => {
    if (billPaymentState) {
      if (
        billPaymentState.scheduleDate &&
        !billPaymentState.scheduleDate.error &&
        billPaymentState.dateOfFirstPayment &&
        !billPaymentState.dateOfFirstPayment.error &&
        billPaymentState.interval &&
        !billPaymentState.interval.error &&
        billPaymentState.paymentEndDate &&
        !billPaymentState.paymentEndDate.error &&
        billPaymentState.bank &&
        !billPaymentState.bank.error &&
        billPaymentState.bankAccountNumber &&
        !billPaymentState.bankAccountNumber.error &&
        billPaymentState.amount &&
        !billPaymentState.amount.error &&
        billPaymentState.wallet &&
        !billPaymentState.wallet.error
      ) {
        setValidated(true);
      } else {
        setValidated(false);
      }
    }
  }, [billPaymentState]);

  useEffect(() => {
    onValidateScheduleForm();
  }, [flagSubmitted, schedule, dateOfFirstPayment, interval, paymentEndDate, bank, bankAccountNumber, amount, walletId]);

  const onValidateScheduleForm = () => {
    if (!flagSubmitted) return;
    let state = { ...billPaymentState };
    switch (scheduleTab) {
      case ScheduleBillPayment.FUTURE: {
        if (_.isEmpty(schedule)) {
          state = {
            ...state,
            ...{
              scheduleDate: { error: true, message: t(`global-bill-payment::glossary.scheduled-date-required-error`) },
              dateOfFirstPayment: { error: false, message: '' },
              interval: { error: false, message: '' },
              paymentEndDate: { error: false, message: '' }
            }
          };
        } else if (schedule.isBefore(moment(), 'day') || schedule.isSame(moment(), 'day')) {
          state = {
            ...state,
            ...{
              scheduleDate: { error: true, message: t(`global-bill-payment::glossary.scheduled-date-invalid-error`) },
              dateOfFirstPayment: { error: false, message: '' },
              interval: { error: false, message: '' },
              paymentEndDate: { error: false, message: '' }
            }
          };
        } else {
          state = {
            ...state,
            ...{
              scheduleDate: { error: false, message: '' },
              dateOfFirstPayment: { error: false, message: '' },
              interval: { error: false, message: '' },
              paymentEndDate: { error: false, message: '' }
            }
          };
        }
        break;
      }
      case ScheduleBillPayment.RECURRING: {
        state = {
          ...state,
          ...{
            scheduleDate: { error: false, message: '' },
            dateOfFirstPayment: {
              error: _.isEmpty(dateOfFirstPayment),
              message: t(`global-bill-payment::glossary.date-first-payment-required-error`)
            },
            interval: { error: _.isEmpty(interval), message: t(`global-bill-payment::glossary.frequency-is-required-error`) },
            paymentEndDate: {
              error: _.isEmpty(paymentEndDate),
              message: t(`global-bill-payment::glossary.payment-end-date-is-required-error`)
            }
          }
        };
        break;
      }
      case ScheduleBillPayment.NOW: {
        state = {
          ...state,
          ...{
            scheduleDate: { error: false, message: '' },
            dateOfFirstPayment: { error: false, message: '' },
            interval: { error: false, message: '' },
            paymentEndDate: { error: false, message: '' }
          }
        };
        break;
      }
      default:
        break;
    }
    if (!bank || _.isEmpty(bank)) {
      state = {
        ...state,
        ...{
          bank: { error: true, message: t(`global-bill-payment::glossary.bank-name-is-required-error`) }
        }
      };
    } else {
      state = {
        ...state,
        ...{
          bank: { error: false, message: undefined }
        }
      };
    }

    if (!bankAccountNumber || _.isEmpty(bankAccountNumber)) {
      state = {
        ...state,
        ...{
          bankAccountNumber: { error: true, message: t(`global-bill-payment::glossary.bank-account-number-is-required-error`) }
        }
      };
    } else {
      state = {
        ...state,
        ...{
          bankAccountNumber: { error: false, message: undefined }
        }
      };
    }

    if (!isAmountPositive(amount)) {
      state = {
        ...state,
        ...{
          amount: { error: true, message: t(`global-bill-payment::glossary.bill-payment-invalid-error`) }
        }
      };
    } else {
      state = {
        ...state,
        ...{
          amount: { error: false, message: undefined }
        }
      };
    }

    if (!walletId || _.isEmpty(walletId) || !debitAccountWallerId || _.isEmpty(debitAccountWallerId)) {
      state = {
        ...state,
        ...{
          wallet: { error: true, message: t(`global-bill-payment::glossary.debit-account-is-required-error`) }
        }
      };
    } else {
      state = {
        ...state,
        ...{
          wallet: { error: false, message: undefined }
        }
      };
    }

    setBillPaymentState({
      ...billPaymentState,
      ...state
    });
  };

  const onValidateVerification = (errorCode?: string): boolean => {
    let validated = false;
    let state = { ...billPaymentState };

    if (!errorCode) return false;
    switch (errorCode) {
      case VERIFICATION_CODE.INSUFFICIENT_BALANCE:
        state = {
          ...state,
          ...{
            amount: { error: true, message: t('global-bill-payment::glossary.insufficient-balance-error') }
          }
        };
        validated = true;
        break;

      case VERIFICATION_CODE.WALLET_NOT_FOUND:
        state = {
          ...state,
          ...{
            wallet: { error: true, message: t('global-bill-payment::glossary.wallet-not-found') }
          }
        };
        validated = true;
        break;
      default:
        break;
    }

    setBillPaymentState({
      ...billPaymentState,
      ...state
    });

    return validated;
  };

  return {
    detailForm: {
      bank,
      bankAccountNumber,
      debitAccountWallerId,
      debitAccountWalletName,
      additionalDetail,
      assetName,
      amount,
      assetList,
      walletId,
      setBankName,
      setTransferPeerId,
      setBankAccountNumber,
      setDebitAccountWallerId,
      setDebitAccountWalletName,
      setAdditionalDetail,
      setAssetName,
      setAmount,
      setWalletId,
      setInstructionId,
      onValidateVerification
    },
    scheduleForm: {
      scheduleTab,
      schedule,
      dateOfFirstPayment,
      interval,
      paymentEndDate,
      remarks,
      setScheduleTab,
      setSchedule,
      setDateOfFirstPayment,
      setInterval,
      setPaymentEndDate,
      onValidateScheduleForm,
      setRemarks
    },
    general: {
      billPaymentState,
      validated,
      quoteDataRequest,
      debitAmount,
      billPaymentFee,
      userFee: bpUserFee,
      isLoadingFee,
      quoteDataRequestProps,
      quoteResponseProps,
      documentSelected,
      setDocumentSelected,
      setQuoteDataRequestProps,
      setQuoteResponseProps,
      setValidated,
      setFlagSubmitted
    }
  };
};
